import React, { useMemo, useRef, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Image } from '@/atoms/Image'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useTranslate } from '@/utils/translate/translate-client'
import { FootballIcon } from './football'

export interface FootballWrapperProps {
  className?: string
  src?: string
  iconWidth?: number
  iconHeight?: number
  imageWidth?: number
  imageHeight?: number
  transforms?: string
}

export const FootballWrapper: React.FC<FootballWrapperProps> = ({
  className,
  src = 'v1757087258/angel-studios/projects/senior/football-player.webp',
  iconWidth = 12,
  iconHeight = 11,
  imageWidth = 25,
  imageHeight = 20,
  transforms = 'q_auto:best,f_auto',
}) => {
  const { t } = useTranslate('common')
  const containerRef = useRef<HTMLDivElement>(null)
  const [travelX, setTravelX] = useState(0)
  const [ready, setReady] = useState(false)

  const playerImageUrl = useMemo(
    () =>
      getCloudinaryImageUrl({
        path: src,
        height: imageHeight,
        transforms,
      }),
    [src, imageHeight, transforms],
  )

  // Use ResizeObserver to handle hidden mobile nav
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new ResizeObserver(() => {
      if (container.offsetWidth > 0) {
        setTravelX(container.offsetWidth - iconWidth)
        setReady(true)
      }
    })

    observer.observe(container)

    return () => observer.disconnect()
  }, [iconWidth])

  const containerClass = ['absolute -top-[2px] flex w-full items-end justify-center', className]
    .filter(Boolean)
    .join(' ')

  // Animation settings
  const riseHeight = -15
  const spinAmount = 2160
  const moveDuration = 2
  const totalCycle = 10

  const animateFootball = (direction: 'left' | 'right') => ({
    x: direction === 'left' ? [0, travelX] : [0, -travelX],
    y: [0, riseHeight, 0],
    rotate: direction === 'left' ? [55, 55 + spinAmount] : [-55, -55 - spinAmount],
  })

  const transitionFootball = {
    x: { duration: moveDuration, ease: 'easeInOut', repeat: Infinity, repeatDelay: totalCycle - moveDuration },
    y: {
      duration: moveDuration,
      ease: ['easeOut', 'easeIn'],
      repeat: Infinity,
      repeatDelay: totalCycle - moveDuration,
    },
    rotate: { duration: moveDuration, ease: 'linear', repeat: Infinity, repeatDelay: totalCycle - moveDuration },
  }

  // Only render motion divs when ready
  if (!ready) {
    return (
      <div ref={containerRef} className={containerClass}>
        <div className="absolute bottom-0 left-0 z-20 shrink-0">
          <FootballIcon width={iconWidth} height={iconHeight} aria-hidden="true" />
        </div>
        <div className="relative z-30 flex items-center justify-center">
          <Image
            src={playerImageUrl}
            alt={t('footballPlayerAlt', 'Football player')}
            width={imageWidth}
            height={imageHeight}
            priority
          />
        </div>
        <div className="absolute bottom-0 right-0 z-20 shrink-0">
          <FootballIcon flip width={iconWidth} height={iconHeight} aria-hidden="true" />
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className={containerClass}>
      <motion.div
        key={travelX} // <-- restart animation when travelX changes
        animate={animateFootball('left')}
        transition={transitionFootball}
        className="absolute bottom-0 left-0 z-20 shrink-0 will-change-transform"
        style={{ transformOrigin: '50% 50%' }}
      >
        <FootballIcon width={iconWidth} height={iconHeight} aria-hidden="true" />
      </motion.div>

      <div className="relative z-30 flex items-center justify-center">
        <Image
          src={playerImageUrl}
          alt={t('footballPlayerAlt', 'Football player')}
          width={imageWidth}
          height={imageHeight}
          priority
        />
      </div>

      <motion.div
        key={`right-${travelX}`} // <-- restart animation
        animate={animateFootball('right')}
        transition={transitionFootball}
        className="absolute bottom-0 right-0 z-20 shrink-0 will-change-transform"
        style={{ transformOrigin: '50% 50%' }}
      >
        <FootballIcon flip width={iconWidth} height={iconHeight} aria-hidden="true" />
      </motion.div>
    </div>
  )
}

export default FootballWrapper
